import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';
import { getDashboardPath } from '../utils/roleUtils';

function ChangePassword({ user }) {
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  });
  const navigate = useNavigate();

  const checkPasswordStrength = (password) => {
    let score = 0;
    const feedback = [];

    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push('At least 8 characters');
    }

    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include lowercase letters');
    }

    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include uppercase letters');
    }

    if (/[0-9]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include numbers');
    }

    if (password.length >= 12) {
      score += 1;
    }

    return { score, feedback };
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPasswords({ ...passwords, newPassword });
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const getStrengthColor = (score) => {
    if (score <= 1) return '#ef4444';
    if (score <= 2) return '#f59e0b';
    if (score <= 3) return '#3b82f6';
    return '#10b981';
  };

  const getStrengthText = (score) => {
    if (score <= 1) return 'Weak';
    if (score <= 2) return 'Fair';
    if (score <= 3) return 'Good';
    return 'Strong';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwords.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await authAPI.changePassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      });
      
      toast.success('Password changed successfully');
      navigate(getDashboardPath(user.permissions, user.role));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container-split">
      {/* Left Side - Gradient with Security Theme */}
      <div className="login-gradient-side">
        <div className="login-gradient-content">
          <div className="login-brand-icon">
            <img 
              src="/assets/logo.png" 
              alt="OUK Logo" 
              className="gradient-logo"
            />
          </div>
          <div style={{ textAlign: 'center', color: 'white', marginTop: '40px' }}>
            <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px' }}>
              🔐 Secure Your Account
            </h2>
            {/* <p style={{ fontSize: '16px', opacity: 0.9, lineHeight: '1.6' }}>
              Update your temporary password to something secure and memorable. 
              Your account security is our priority.
            </p> */}
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: '12px', 
              padding: '20px', 
              marginTop: '30px',
              backdropFilter: 'blur(10px)'
            }}>
              <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Security Tips:</h3>
              <ul style={{ textAlign: 'left', fontSize: '14px', opacity: 0.9 }}>
                <li style={{ marginBottom: '8px' }}>Use a mix of letters, numbers</li>
                <li style={{ marginBottom: '8px' }}>Make it at least 8 characters long</li>
                <li style={{ marginBottom: '8px' }}>Avoid common words or patterns</li>
                <li>Don't reuse old passwords</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="login-gradient-shapes">
          <div className="gradient-shape shape-1"></div>
          <div className="gradient-shape shape-2"></div>
          <div className="gradient-shape shape-3"></div>
          <div className="gradient-shape shape-4"></div>
        </div>
      </div>

      {/* Right Side - Change Password Form */}
      <div className="login-form-side">
        <div className="login-form-container">
          <div className="login-form-header">
            <div className="login-form-icon">
              <img 
                src="/assets/logo.png" 
                alt="OUK Logo" 
                className="login-logo"
              />
            </div>
            <h2 className="login-form-title">Change Password</h2>
            <p style={{ color: '#64748b', fontSize: '14px', textAlign: 'center', marginTop: '8px' }}>
              Welcome, <strong>{user?.username}</strong>! Please update your temporary password.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <div className="password-input-container">
                <input
                  type={showPasswords.current ? "text" : "password"}
                  className="login-input"
                  placeholder="Enter your current password"
                  value={passwords.currentPassword}
                  onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                  required
                />
                <button 
                  type="button" 
                  className="password-toggle"
                  onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                >
                  <span>{showPasswords.current ? '🙈' : '👁'}</span>
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">New Password</label>
              <div className="password-input-container">
                <input
                  type={showPasswords.new ? "text" : "password"}
                  className="login-input"
                  placeholder="Enter your new password"
                  value={passwords.newPassword}
                  onChange={handlePasswordChange}
                  required
                />
                <button 
                  type="button" 
                  className="password-toggle"
                  onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                >
                  <span>{showPasswords.new ? '🙈' : '👁'}</span>
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {passwords.newPassword && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>
                      Password Strength
                    </span>
                    <span style={{ 
                      fontSize: '12px', 
                      fontWeight: '700', 
                      color: getStrengthColor(passwordStrength.score) 
                    }}>
                      {getStrengthText(passwordStrength.score)}
                    </span>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '6px', 
                    backgroundColor: '#f1f5f9', 
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${(passwordStrength.score / 5) * 100}%`, 
                      height: '100%', 
                      backgroundColor: getStrengthColor(passwordStrength.score),
                      transition: 'all 0.3s ease',
                      borderRadius: '3px'
                    }}></div>
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      {passwordStrength.feedback.map((tip, index) => (
                        <div key={index} style={{ 
                          fontSize: '11px', 
                          color: '#f59e0b', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px',
                          marginBottom: '2px'
                        }}>
                          <span>⚠</span>
                          <span>{tip}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <div className="password-input-container">
                <input
                  type={showPasswords.confirm ? "text" : "password"}
                  className={`login-input ${passwords.confirmPassword && passwords.newPassword !== passwords.confirmPassword ? 'error' : passwords.confirmPassword && passwords.newPassword === passwords.confirmPassword ? 'success' : ''}`}
                  placeholder="Confirm your new password"
                  value={passwords.confirmPassword}
                  onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                  required
                />
                <button 
                  type="button" 
                  className="password-toggle"
                  onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                >
                  <span>{showPasswords.confirm ? '🙈' : '👁'}</span>
                </button>
              </div>
              {passwords.confirmPassword && passwords.newPassword !== passwords.confirmPassword && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#ef4444', 
                  marginTop: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>❌</span>
                  <span>Passwords do not match</span>
                </div>
              )}
              {passwords.confirmPassword && passwords.newPassword === passwords.confirmPassword && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#10b981', 
                  marginTop: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>✅</span>
                  <span>Passwords match</span>
                </div>
              )}
            </div>
            
            <button 
              type="submit" 
              className="login-submit-btn" 
              disabled={loading || passwords.newPassword !== passwords.confirmPassword || passwords.newPassword.length < 8}
              style={{
                opacity: (loading || passwords.newPassword !== passwords.confirmPassword || passwords.newPassword.length < 8) ? 0.6 : 1
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>🔄</span>
                  <span>Updating Password...</span>
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>🔐</span>
                  <span>Update Password</span>
                </span>
              )}
            </button>

            <div style={{ 
              textAlign: 'center', 
              marginTop: '20px', 
              padding: '16px', 
              backgroundColor: '#f8fafc', 
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                🛡️ <strong>Security Notice:</strong> After changing your password, you'll be redirected to your dashboard. 
                Please remember your new password for future logins.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChangePassword;
